import { ApolloError } from '@apollo/client';

import { CreateTaskMutationVariables } from '@graphql/generated';
import { mutationFailed } from '@utils/graphqlError';

// createTask raises this when any `dependencyIds` entry is a task the caller
// can't see (TA-11150). Its code is the generic RESOURCE_NOT_FOUND, shared
// with "Project not found." and the rest, so only the message tells it apart.
export const DEPENDENCY_NOT_FOUND_MESSAGE =
  'One or more dependency tasks could not be found.';

/**
 * Whether a createTask failure is the dependency rejection above. Takes an
 * `ApolloError` (the hook's `error`) or the `GraphQLError[]` a resolved
 * mutation result carries under `errorPolicy: 'all'`.
 */
export const isDependencyNotFoundError = (errors: unknown): boolean => {
  let list: readonly unknown[] = [];
  if (errors instanceof ApolloError) list = errors.graphQLErrors;
  else if (Array.isArray(errors)) list = errors;
  return (list as ({ message?: unknown } | null | undefined)[]).some(
    (e: { message?: unknown } | null | undefined) =>
      e?.message === DEPENDENCY_NOT_FOUND_MESSAGE
  );
};

/**
 * TA-11209: runs createTask for a copied task. A copy takes its
 * `dependencyIds` from the source task, which can come from a persisted cache
 * written before the backend started hiding dependencies the viewer can't see
 * (TA-11197). The backend rejects the whole call over such an id, and the
 * user has no way to see or remove it. So when a copy fails for that reason,
 * retry once without dependencies; `dependenciesDropped` tells the caller to
 * let the user know.
 *
 * Only copies retry. Dependencies a user picked by hand are never dropped.
 *
 * `onRetryWithoutDependencies` runs just before the retry is sent, so a
 * caller can record it before the retry's result reaches any effect.
 */
export const createTaskDroppingHiddenDependencies = async <
  TResult extends { errors?: unknown },
>({
  variables,
  isCopy,
  create,
  onRetryWithoutDependencies,
}: {
  variables: CreateTaskMutationVariables;
  isCopy: boolean;
  create: (variables: CreateTaskMutationVariables) => Promise<TResult>;
  onRetryWithoutDependencies?: () => void;
}): Promise<{ result: TResult; dependenciesDropped: boolean }> => {
  const result = await create(variables);
  if (
    !isCopy ||
    !variables.attributes.dependencyIds?.length ||
    !mutationFailed(result) ||
    !isDependencyNotFoundError(result.errors)
  ) {
    return { result, dependenciesDropped: false };
  }

  onRetryWithoutDependencies?.();
  const retry = await create({
    ...variables,
    attributes: { ...variables.attributes, dependencyIds: [] },
  });
  return { result: retry, dependenciesDropped: !mutationFailed(retry) };
};

/**
 * Where to tell the user a copy dropped its dependencies. It goes inside the
 * "Task created" toast when that toast shows: react-native-toast-notifications
 * builds its list from `this.state` inside requestAnimationFrame, so two
 * `show()` calls in the same frame overwrite each other and one toast is lost.
 * A separate toast is only safe when "Task created" isn't shown.
 */
export const dependencyNoticePlacement = ({
  dependenciesDropped,
  showsTaskCreatedToast,
}: {
  dependenciesDropped: boolean;
  showsTaskCreatedToast: boolean;
}): 'none' | 'taskCreatedToast' | 'separateToast' => {
  if (!dependenciesDropped) return 'none';
  return showsTaskCreatedToast ? 'taskCreatedToast' : 'separateToast';
};
